import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'primereact/dropdown';
import styles from './stylesheets/CallSettings.module.css';
import {
  wcaGetCallSettings,
  wcaSetCallSettings,
} from '../../../reducers/wca/setting/setting.action';
import { CustomButton } from '../../../components/CustomButton';
import Loader from '../../../components/Loader';
import { useLayout } from '../../../context/LayoutContext';

const CallSettings = ({ metadataId }) => {
  const dispatch = useDispatch();
  const { toast } = useLayout();
  const { callSettings, loading, error } = useSelector((state) => state.wcaSettings);

  const [editableCallSettings, setEditableCallSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const statusOptions = [
    { label: 'Enabled', value: 'ENABLED' },
    { label: 'Disabled', value: 'DISABLED' },
  ];

  const callIconOptions = [
    { label: 'Default', value: 'DEFAULT' },
    { label: 'Shown', value: 'SHOWN' },
    { label: 'Hidden', value: 'HIDDEN' },
  ];

  const callbackOptions = [
    { label: 'Enabled', value: 'ENABLED' },
    { label: 'Disabled', value: 'DISABLED' },
  ];

  useEffect(() => {
    if (metadataId) {
      fetchCallSettings();
    }
  }, [metadataId]);

  const fetchCallSettings = async () => {
    try {
      await dispatch(wcaGetCallSettings({ metadataid: metadataId })).unwrap();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (callSettings?.calling) {
      setEditableCallSettings(callSettings);
    }
  }, [callSettings]);

  const handleChange = (field, value) => {
    setEditableCallSettings((prev) => ({
      ...prev,
      calling: {
        ...prev.calling,
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (!editableCallSettings) return;

    setSaving(true);

    try {
      await dispatch(
        wcaSetCallSettings({
          metadataid: metadataId,
          data: editableCallSettings,
        }),
      ).unwrap();

      toast.current?.show({
        severity: 'success',
        detail: 'Call settings saved successfully!',
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        detail: 'Failed to save call settings',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <Loader visible={loading} />

      {editableCallSettings && (
        <>
          <div className={styles.formGrid}>
            {/* STATUS */}
            <div className={styles.formGroup}>
              <label>Status</label>
              <Dropdown
                value={editableCallSettings.calling.status}
                options={statusOptions}
                onChange={(e) => handleChange('status', e.value)}
                placeholder="Select Status"
                className="w-full"
              />
            </div>

            {/* CALL ICON */}
            <div className={styles.formGroup}>
              <label>Call Icon Visibility</label>
              <Dropdown
                value={editableCallSettings.calling.call_icon_visibility || 'DEFAULT'}
                options={callIconOptions}
                onChange={(e) => handleChange('call_icon_visibility', e.value)}
                placeholder="Select Visibility"
                className="w-full"
              />
            </div>

            {/* CALLBACK */}
            <div className={styles.formGroup}>
              <label>Callback Permission</label>
              <Dropdown
                value={editableCallSettings.calling.callback_permission_status || 'ENABLED'}
                options={callbackOptions}
                onChange={(e) => handleChange('callback_permission_status', e.value)}
                placeholder="Select Callback Permission"
                className="w-full"
              />
            </div>
          </div>

          <div className={styles.actions}>
            <CustomButton
              label={saving ? 'Saving...' : 'Apply Changes'}
              loading={saving}
              onClick={handleSave}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CallSettings;
